import { z } from 'zod';

/**
 * What a journey can put on a board in one go, as data.
 *
 * Two kinds:
 * - Library groups: finished groups that live on Poppy's own template boards (their videos
 *   already transcribed), copied onto the person's board. The app owns where each one lives
 *   (lib/coach/groupSources.ts, typed against this list, so none can be missing).
 * - Templates: a starter layout (groups of links and notes, an optional chat wired to all
 *   of it, and library groups beside it). The app lays one out through the same board
 *   operation the Copilot uses (`add_to_board`), so it is one step of the person's undo.
 */

export const COACH_LIBRARY_GROUPS = {
  'titles-and-scripts-long-form-group': { label: 'YouTube titles and scripts framework' },
  'hooks-and-scripts-short-form-group': { label: 'IG/TikTok hooks and scripts framework' },
  'linkedin-content-framework-group': { label: 'LinkedIn post framework' },
  'vsl-video-sales-letter-framework-group': { label: 'Video sales letter framework' },
  'viral-ad-copy-framework-group': { label: 'Ad copy framework' },
  'wbbc-top-posts': { label: "Simon Squibb's top 10 posts (WBBC)" },
  'wbbc-ready-pictures': { label: 'Ready-to-post WBBC pictures' },
} as const satisfies Record<string, { label: string }>;

export type CoachLibraryGroupId = keyof typeof COACH_LIBRARY_GROUPS;

export const COACH_LIBRARY_GROUP_IDS = Object.keys(COACH_LIBRARY_GROUPS) as CoachLibraryGroupId[];

export const LibraryGroupIdSchema = z.enum(
  COACH_LIBRARY_GROUP_IDS as [CoachLibraryGroupId, ...CoachLibraryGroupId[]]
);

/** One thing in a template group: a link Poppy reads (video, post, site), or a note. */
export const CoachTemplateItemSchema = z.union([
  z.object({ url: z.string().url(), title: z.string().min(1).optional() }).strict(),
  z.object({ title: z.string().min(1), text: z.string().min(1) }).strict(),
]);

export const CoachTemplateGroupSchema = z
  .object({
    title: z.string().min(1),
    /** Large type above the group. */
    heading: z.string().min(1).optional(),
    items: z.array(CoachTemplateItemSchema).min(1),
  })
  .strict();

export const CoachTemplateSchema = z
  .object({
    label: z.string().min(1),
    description: z.string().min(1),
    groups: z.array(CoachTemplateGroupSchema).default([]),
    /** Finished groups from Poppy's template boards, copied in beside the groups. */
    libraryGroups: z.array(LibraryGroupIdSchema).default([]),
    /** A chat beside it all, wired to every group and framework. */
    chat: z
      .object({ title: z.string().min(1) })
      .strict()
      .optional(),
  })
  .strict()
  .refine(template => template.groups.length + template.libraryGroups.length > 0, {
    message: 'a template needs at least one group or library group',
  });

export type CoachTemplateInput = z.input<typeof CoachTemplateSchema>;
export type CoachTemplate = z.output<typeof CoachTemplateSchema>;

const templates = {
  'short-form-starter': {
    label: 'Short-form starter',
    description: 'Your brand notes and the IG/TikTok hooks framework, wired to a script chat.',
    groups: [
      {
        title: 'Your brand',
        heading: 'Start here',
        items: [
          {
            title: 'Who I make content for',
            text: 'Replace this with who you are talking to, what they want, and what they struggle with. Your chat reads this before every script.',
          },
          {
            title: 'What I sell or teach',
            text: 'Replace this with your offer, your main topics, and the one thing you want viewers to do next.',
          },
        ],
      },
    ],
    libraryGroups: ['hooks-and-scripts-short-form-group'],
    chat: { title: 'Script writer' },
  },
  'youtube-starter': {
    label: 'YouTube starter',
    description: 'Your channel notes and the YouTube titles framework, wired to a script chat.',
    groups: [
      {
        title: 'Your channel',
        heading: 'Start here',
        items: [
          {
            title: 'Who my channel is for',
            text: 'Replace this with your viewer, the problem they search for, and why they should pick your channel.',
          },
        ],
      },
    ],
    libraryGroups: ['titles-and-scripts-long-form-group'],
    chat: { title: 'YouTube writer' },
  },
} as const satisfies Record<string, CoachTemplateInput>;

export type CoachTemplateId = keyof typeof templates;

/**
 * Every valid template, parsed. `templates.test.ts` proves each one is valid; one that is not
 * is left out with a console error instead of throwing, as journeys are (journeys/index.ts),
 * so a bad template can never break the product code that imports this.
 */
export const COACH_TEMPLATES: Partial<Record<CoachTemplateId, CoachTemplate>> = Object.fromEntries(
  Object.entries(templates).flatMap(([id, template]) => {
    const parsed = CoachTemplateSchema.safeParse(template);
    if (parsed.success) return [[id, parsed.data]];
    console.error(`Invalid coach template "${id}":`, parsed.error.issues);
    return [];
  })
);

export const COACH_TEMPLATE_IDS = Object.keys(templates) as CoachTemplateId[];

export const TemplateIdSchema = z.enum(
  COACH_TEMPLATE_IDS as [CoachTemplateId, ...CoachTemplateId[]]
);
